import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as shopApi from '../../services/shopApi';

// Async thunks
export const fetchProducts = createAsyncThunk(
  'shop/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      const data = await shopApi.getProducts();
      return data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

export const fetchPublicProducts = createAsyncThunk(
  'shop/fetchPublicProducts',
  async (_, { rejectWithValue }) => {
    try {
      const data = await shopApi.getPublicProducts();
      return data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

export const fetchMyPurchases = createAsyncThunk(
  'shop/fetchMyPurchases',
  async (_, { rejectWithValue }) => {
    try {
      const data = await shopApi.getMyPurchases();
      return data.purchases;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch purchases');
    }
  }
);

export const fetchMyAccess = createAsyncThunk(
  'shop/fetchMyAccess',
  async (_, { rejectWithValue }) => {
    try {
      const data = await shopApi.getMyAccess();
      return data.access;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch access');
    }
  }
);

export const purchaseWithStripe = createAsyncThunk(
  'shop/purchaseWithStripe',
  async (productId, { rejectWithValue }) => {
    try {
      const data = await shopApi.createStripeCheckout(productId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create checkout');
    }
  }
);

export const purchaseWithCash = createAsyncThunk(
  'shop/purchaseWithCash',
  async (productId, { rejectWithValue }) => {
    try {
      const data = await shopApi.createCashOrder(productId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to place order');
    }
  }
);

export const fetchProductDetails = createAsyncThunk(
  'shop/fetchProductDetails',
  async (productId, { rejectWithValue }) => {
    try {
      const data = await shopApi.getPublicProduct(productId);
      return data.product;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch product');
    }
  }
);

export const fetchTrainerProducts = createAsyncThunk(
  'shop/fetchTrainerProducts',
  async (_, { rejectWithValue }) => {
    try {
      const data = await shopApi.getTrainerProducts();
      return data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

export const fetchAllProducts = createAsyncThunk(
  'shop/fetchAllProducts',
  async (_, { rejectWithValue }) => {
    try {
      const data = await shopApi.getAdminProducts();
      return data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

export const createProduct = createAsyncThunk(
  'shop/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const data = await shopApi.createProduct(productData);
      return data.product;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create product');
    }
  }
);

export const updateProduct = createAsyncThunk(
  'shop/updateProduct',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await shopApi.updateProduct(id, data);
      return result.product;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update product');
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'shop/deleteProduct',
  async (productId, { rejectWithValue }) => {
    try {
      await shopApi.deleteProduct(productId);
      return productId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete product');
    }
  }
);

export const fetchPendingCashOrders = createAsyncThunk(
  'shop/fetchPendingCashOrders',
  async (_, { rejectWithValue }) => {
    try {
      const data = await shopApi.getPendingCashOrders();
      return data.pendingOrders || data.orders || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch orders');
    }
  }
);

export const confirmCashPayment = createAsyncThunk(
  'shop/confirmCashPayment',
  async (purchaseId, { rejectWithValue }) => {
    try {
      const data = await shopApi.confirmCashPayment(purchaseId);
      return data.purchase;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to confirm payment');
    }
  }
);

export const createCashOrder = createAsyncThunk(
  'shop/createCashOrder',
  async (productId, { rejectWithValue }) => {
    try {
      const data = await shopApi.createCashOrder(productId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to place order');
    }
  }
);

export const fetchTrainersWithProducts = createAsyncThunk(
  'shop/fetchTrainersWithProducts',
  async (_, { rejectWithValue }) => {
    try {
      const data = await shopApi.getTrainersWithProducts();
      return data.trainers;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch trainers');
    }
  }
);

const initialState = {
  trainerProducts: [],
  allProducts: [],
  pendingCashOrders: [],
  products: [],
  trainersWithProducts: [],
  currentProduct: null,
  purchases: [],
  myAccess: [],
  loading: false,
  error: null,
  purchaseLoading: false,
  purchaseError: null
};

const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    setCurrentProduct: (state, action) => {
      state.currentProduct = action.payload;
    },
    clearError: (state) => {
      state.error = null;
      state.purchaseError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch public products
      .addCase(fetchPublicProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPublicProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchPublicProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch my purchases
      .addCase(fetchMyPurchases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyPurchases.fulfilled, (state, action) => {
        state.loading = false;
        state.purchases = action.payload;
      })
      .addCase(fetchMyPurchases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch my access
      .addCase(fetchMyAccess.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyAccess.fulfilled, (state, action) => {
        state.loading = false;
        state.myAccess = action.payload;
      })
      .addCase(fetchMyAccess.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Purchase with Stripe
      .addCase(purchaseWithStripe.pending, (state) => {
        state.purchaseLoading = true;
        state.purchaseError = null;
      })
      .addCase(purchaseWithStripe.fulfilled, (state) => {
        state.purchaseLoading = false;
      })
      .addCase(purchaseWithStripe.rejected, (state, action) => {
        state.purchaseLoading = false;
        state.purchaseError = action.payload;
      })
      
      // Purchase with cash
      .addCase(purchaseWithCash.pending, (state) => {
        state.purchaseLoading = true;
        state.purchaseError = null;
      })
      .addCase(purchaseWithCash.fulfilled, (state) => {
        state.purchaseLoading = false;
      })
      .addCase(purchaseWithCash.rejected, (state, action) => {
        state.purchaseLoading = false;
        state.purchaseError = action.payload;
      })
      
      // Fetch product details
      .addCase(fetchProductDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProductDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(fetchProductDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch trainer products
      .addCase(fetchTrainerProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTrainerProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.trainerProducts = action.payload;
      })
      .addCase(fetchTrainerProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch all products (admin)
      .addCase(fetchAllProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.allProducts = action.payload;
      })
      .addCase(fetchAllProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create product
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.trainerProducts.push(action.payload);
        state.allProducts.push(action.payload);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update product
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.trainerProducts.findIndex(p => p.id === action.payload.id);
        if (index !== -1) state.trainerProducts[index] = action.payload;
        const allIndex = state.allProducts.findIndex(p => p.id === action.payload.id);
        if (allIndex !== -1) state.allProducts[allIndex] = action.payload;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete product
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.trainerProducts = state.trainerProducts.filter(p => p.id !== action.payload);
        state.allProducts = state.allProducts.filter(p => p.id !== action.payload);
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch pending cash orders
      .addCase(fetchPendingCashOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPendingCashOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingCashOrders = action.payload;
      })
      .addCase(fetchPendingCashOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Confirm cash payment
      .addCase(confirmCashPayment.pending, (state) => {
        state.loading = true;
      })
      .addCase(confirmCashPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingCashOrders = state.pendingCashOrders.filter(o => o.id !== action.payload.id);
      })
      .addCase(confirmCashPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create cash order
      .addCase(createCashOrder.pending, (state) => {
        state.purchaseLoading = true;
        state.purchaseError = null;
      })
      .addCase(createCashOrder.fulfilled, (state) => {
        state.purchaseLoading = false;
      })
      .addCase(createCashOrder.rejected, (state, action) => {
        state.purchaseLoading = false;
        state.purchaseError = action.payload;
      })
      
      // Fetch trainers with products
      .addCase(fetchTrainersWithProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrainersWithProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.trainersWithProducts = action.payload;
      })
      .addCase(fetchTrainersWithProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setCurrentProduct, clearError } = shopSlice.actions;

// Selectors
export const selectProducts = (state) => state.shop.products;
export const selectTrainerProducts = (state) => state.shop.trainerProducts;
export const selectAllProducts = (state) => state.shop.allProducts;
export const selectPendingCashOrders = (state) => state.shop.pendingCashOrders;
export const selectTrainersWithProducts = (state) => state.shop.trainersWithProducts;
export const selectCurrentProduct = (state) => state.shop.currentProduct;
export const selectPurchases = (state) => state.shop.purchases;
export const selectMyAccess = (state) => state.shop.myAccess;
export const selectLoading = (state) => state.shop.loading;
export const selectError = (state) => state.shop.error;
export const selectPurchaseLoading = (state) => state.shop.purchaseLoading;
export const selectPurchaseError = (state) => state.shop.purchaseError;

export default shopSlice.reducer;
